import { 
  getFirestore, 
  collection, 
  query, 
  where, 
  getDocs, 
  or,
  onSnapshot 
} from "firebase/firestore";
import { firebaseApp } from "../../firebase.js"; // adjust path if needed

const db = getFirestore(firebaseApp);

/**
 * Fetch forms where the given user UID is either the admin or a subadmin,
 * and include all response documents in a `responses` array.
 * 
 * @param {string} userUid - The UID of the current user
* @param {(forms: Array) => void} callback - Function called with updated forms
 * @returns {Function} unsubscribe - Call to stop listening
 */
export async function getFormsForUser(userUid) {
  try {
    const formsRef = collection(db, "forms");

    const q = query(
      formsRef,
      or(
        where("admin", "==", userUid),
        where("subadmin", "array-contains", userUid)
      )
    );

    const querySnapshot = await getDocs(q);

    const forms = await Promise.all(
      querySnapshot.docs.map(async (docSnap) => {
        const formData = { id: docSnap.id, ...docSnap.data() };

        // fetch subcollection responses
        const responsesRef = collection(db, "forms", docSnap.id, "responses");
        const responsesSnap = await getDocs(responsesRef);
        const responses = responsesSnap.docs.map((r) => ({
          id: r.id,
          ...r.data(),
        }));

        return { ...formData, responses };
      })
    );

    return forms;
  } catch (error) {
    console.error("Error fetching forms with responses:", error);
    return [];
  }
}

export function listenFormsForUser(userUid, callback) {
  const formsRef = collection(db, "forms");

  const q = query(
    formsRef,
    or(
      where("admin", "==", userUid),
      where("subadmin", "array-contains", userUid)
    )
  );

  const unsubscribe = onSnapshot(q, (querySnapshot) => {
    querySnapshot.docs.forEach(async (docSnap) => {
      const formData = { id: docSnap.id, ...docSnap.data() };

      // If you ALSO want responses live:
      const responsesRef = collection(db, "forms", docSnap.id, "responses");
      onSnapshot(responsesRef, (responsesSnap) => {
        const responses = responsesSnap.docs.map((r) => ({
          id: r.id,
          ...r.data(),
        }));
        callback(prev => {
          const otherForms = prev.filter(f => f.id !== docSnap.id);
          return [...otherForms, { ...formData, responses }];
        });
      });
    });
  });

  return unsubscribe;
}
